import classNames from 'classnames'
import { useRef } from 'react'
import { useInView } from 'react-intersection-observer'

import { Canvas } from '../../components/canvas'
import { GetStepButton } from '../../components/get-step-button'
import { HeroQRCode } from '../../components/hero-qrcode'
import { Container } from '../../components/layout/grid'
import { Markdown } from '../../components/markdown'
import { Desktop, Mobile } from '../../components/responsive'
import { Text } from '../../components/text'
import { TokenIcon } from '../../components/token-icon'
import { DEFAULT_DRIFT } from '../../components/token-icon/constants'
import { DownloadApp } from '../../contentful-components/forms/download-app'
import type { HomepageHeroFragment } from '../../graphql/fragments/HomepageHeroFragment.graphql'
import { useAnimationFrame } from '../../lib/hooks/useAnimationFrame'
import useRefs from '../../lib/hooks/useRefs'
import { useScroll } from '../../lib/hooks/useScroll'
import { containsVideoSource } from '../../lib/utils/containsVideoSource'
import { getHeaderHeight } from '../../lib/utils/getHeaderHeight'
import { range } from '../../lib/utils/range'
import { TEXT } from '../../lib/utils/text'
import { withMouse } from '../../lib/utils/withMouse'
import { withScroll } from '../../lib/utils/withScroll'
import { primaryTokenScales, secondaryTokenScales } from './constants'
import styles from './homepage-hero.module.scss'
import { HomepageHeroComposition } from './HomepageHeroComposition'

const primaryTokens: [string, string, string, string] = [
    'stars',
    'dollar_sign',
    'dollar_sign',
    'globe',
]
const secondaryTokens: [string, string, string, string] = [
    'dollar_sign',
    'stars',
    'dollar_sign',
    'stars',
]

export const disclaimer =
    'Banking services provided by Evolve Bank & Trust, Member FDIC. Step is a financial technology company, not a bank.'

const screens: [string, string] = [
    'assets/homepage/home-app-1.png',
    'assets/homepage/home-app-2.png',
]

const QRCodeLink = 'https://get.step.com/rM7X/ljuz7o1w'

const items = [
    {
        image: '',
        heading: '**Earn 5.00% on your savings^1**',
        copy: '**With a qualifying direct deposit**',
    },

    {
        image: 'images/fdic.svg',
        copy: 'Banking services are provided by Evolve Bank & Trust and FDIC-insured up to *$250,000¹*',
        width: 132,
        height: 54,
        alt: 'fdic insured',
    },
    {
        image: 'images/visa.svg',
        copy: 'Powered by Visa with built-in fraud protection and *Zero Liability³* guarantee for unauthorized charges',
        width: 167,
        height: 52,
        alt: 'powered by visa',
    },
    {
        image: '',
        heading: '**The best way to build credit history^2**',
        copy: '**(even before you turn 18)**',
    },
]

export const HomepageHero = withMouse<HomepageHeroProps>(
    withScroll<HomepageHeroProps>(
        ({ headingLeft, headingRight, headingRightLine2, tagline }) => {
            const screensHaveVideo = containsVideoSource(screens)

            const containerRef = useRef<HTMLDivElement | null>(null)
            const canvasContainerRef = useRef<HTMLDivElement | null>(null)
            const [primaryInViewRef, primaryInView] = useInView()
            const [secondaryInViewRef, secondaryInView] = useInView()
            const { controller: scroll } = useScroll()

            const itemRefs = useRefs(items)
            const iconRefs = useRefs(secondaryTokens.slice())

            const headingTypeStyle = {
                _: 'heading-60',
                md: 'heading-2xl',
                lg: 'heading-120',
            }

            // update text items
            useAnimationFrame(() => {
                const firstSetOfIconsLoaded =
                    itemRefs[0].current && itemRefs[1].current

                if (firstSetOfIconsLoaded) {
                    const progress = scroll.get().progress

                    const ranges = [
                        range(progress, 0.0, 0.5),
                        range(progress, 0.5, 0.27),
                    ]
                    const opacities = ranges.map((r) =>
                        r > 0 && r < 1 ? 1 : 0
                    )
                    const iconOpacities = ranges.map((r) =>
                        r > 0.2 && r < 1 ? 1 : 0
                    )

                    itemRefs[0].current.style.opacity = opacities[0]
                    itemRefs[1].current.style.opacity = opacities[0]
                    itemRefs[2].current.style.opacity = opacities[1]
                    itemRefs[3].current.style.opacity = opacities[1]

                    // icon opacity
                    iconRefs[0].current.style.opacity = iconOpacities[0]
                    iconRefs[1].current.style.opacity = iconOpacities[0]
                    iconRefs[2].current.style.opacity = iconOpacities[1]
                    iconRefs[3].current.style.opacity = iconOpacities[1]
                }
            }, secondaryInView)

            return (
                <div ref={containerRef} style={{ position: 'relative' }}>
                    <div
                        ref={primaryInViewRef}
                        className={styles['primary-container']}
                    >
                        <Container className={styles['primary-copy']}>
                            <h1 className={styles.h1}>
                                <div
                                    className={classNames(
                                        styles['heading-container'],
                                        styles[
                                            `heading-container--left-container`
                                        ]
                                    )}
                                >
                                    <div className={styles['token-anchor']}>
                                        <TokenIcon
                                            type={primaryTokens[0]}
                                            key={`primary-token--${primaryTokens[0]}--0`}
                                            className={
                                                styles[`primary-token-0`]
                                            }
                                            color='tertiary'
                                            scale={primaryTokenScales[0]}
                                            drift={DEFAULT_DRIFT}
                                        />
                                    </div>
                                    <Markdown
                                        color='primary'
                                        tag='div'
                                        typeStyle={headingTypeStyle}
                                        markdown={headingLeft}
                                    />
                                    <div className={styles['token-anchor']}>
                                        <TokenIcon
                                            type={primaryTokens[3]}
                                            key={`primary-token--${primaryTokens[3]}--3`}
                                            className={
                                                styles[`primary-token-3`]
                                            }
                                            color='tertiary'
                                            scale={primaryTokenScales[3]}
                                            drift={DEFAULT_DRIFT}
                                        />
                                    </div>
                                </div>
                                <div
                                    className={classNames(
                                        styles['heading-container'],
                                        styles[
                                            `heading-container--right-container`
                                        ]
                                    )}
                                >
                                    <div
                                        className={
                                            styles[
                                                `heading-container--right-container-first-line`
                                            ]
                                        }
                                    >
                                        <div className={styles['token-anchor']}>
                                            <TokenIcon
                                                type={primaryTokens[1]}
                                                key={`primary-token--${primaryTokens[1]}--1`}
                                                className={
                                                    styles[`primary-token-1`]
                                                }
                                                color='tertiary'
                                                scale={primaryTokenScales[1]}
                                                drift={DEFAULT_DRIFT}
                                            />
                                            <TokenIcon
                                                type={primaryTokens[2]}
                                                key={`primary-token--${primaryTokens[2]}--2`}
                                                className={
                                                    styles[`primary-token-2`]
                                                }
                                                color='tertiary'
                                                scale={primaryTokenScales[2]}
                                                drift={DEFAULT_DRIFT}
                                            />
                                        </div>
                                        <Markdown
                                            color='primary'
                                            tag='div'
                                            typeStyle={headingTypeStyle}
                                            markdown={headingRight}
                                        />
                                    </div>
                                    <div
                                        className={
                                            styles[
                                                `heading-container--right-container-second-line`
                                            ]
                                        }
                                    >
                                        <Markdown
                                            color='primary'
                                            tag='div'
                                            typeStyle={headingTypeStyle}
                                            markdown={headingRightLine2}
                                        />
                                    </div>
                                </div>
                            </h1>
                            <div
                                className={
                                    styles['heading-container-bottom-section']
                                }
                            >
                                <div className={styles['cta-container']}>
                                    <Mobile>
                                        <GetStepButton />
                                    </Mobile>
                                    <Desktop>
                                        <HeroQRCode
                                            hasBackground={false}
                                            url={QRCodeLink}
                                        />
                                    </Desktop>
                                </div>

                                <div className={styles['copy-container']}>
                                    <Text
                                        typeStyle={{
                                            _: 'body-xs',
                                            md: 'body-md',
                                            lg: 'body-xl',
                                        }}
                                        color='white'
                                    >
                                        {tagline}
                                    </Text>

                                    <Desktop>
                                        <DownloadApp
                                            title={TEXT.getStarted}
                                            forceExpanded
                                            placement='section'
                                        />
                                    </Desktop>
                                </div>
                            </div>
                        </Container>
                    </div>
                    <Container>
                        <div
                            ref={secondaryInViewRef}
                            className={styles['secondary-container']}
                            style={{
                                height: `${
                                    (Math.round(items.length) / 2) * 100
                                }vh`,
                            }}
                        >
                            <div className={styles['secondary-inner']}>
                                {secondaryTokens.map((type, index) => (
                                    <div
                                        ref={iconRefs[index]}
                                        key={`secondary-icon--${type}--${index}`}
                                        className={classNames(
                                            styles[`secondary-icon`],
                                            styles[`secondary-icon-${index}`]
                                        )}
                                    >
                                        <TokenIcon
                                            type={type}
                                            color='cyanora'
                                            scale={secondaryTokenScales[index]}
                                            drift={DEFAULT_DRIFT}
                                        />
                                    </div>
                                ))}
                                {items.map(
                                    (
                                        {
                                            image,
                                            copy,
                                            heading,
                                            width,
                                            height,
                                            alt,
                                        },
                                        itemIndex
                                    ) => (
                                        <div
                                            key={`hero-item--${itemIndex}`}
                                            ref={itemRefs[itemIndex]}
                                            className={classNames(
                                                styles['item'],
                                                styles[`item-${itemIndex}`],
                                                heading
                                                    ? styles[
                                                          `item-with-heading`
                                                      ]
                                                    : ''
                                            )}
                                        >
                                            {heading && (
                                                <Markdown
                                                    accentColor='cyanora'
                                                    color='cyanora'
                                                    markdown={heading}
                                                    typeStyle={{
                                                        _: 'heading-sm',
                                                        md: 'heading-lg',
                                                    }}
                                                />
                                            )}
                                            {image && (
                                                <img
                                                    alt={alt}
                                                    src={image}
                                                    width={width}
                                                    height={height}
                                                />
                                            )}
                                            {copy && (
                                                <div>
                                                    <Markdown
                                                        accentColor='cyanora'
                                                        markdown={copy}
                                                        typeStyle={{
                                                            _: 'body-xs',
                                                            md: 'body-lg',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </Container>

                    <div
                        className={classNames(styles['canvas-container-outer'])}
                    >
                        <div
                            ref={canvasContainerRef}
                            className={styles['canvas-container']}
                        >
                            <Canvas
                                frameloop={
                                    screensHaveVideo ? 'always' : 'demand'
                                }
                            >
                                <HomepageHeroComposition
                                    inView={primaryInView || secondaryInView}
                                    screens={screens}
                                />
                            </Canvas>
                        </div>
                    </div>

                    <div className={styles['disclaimer-container-outer']}>
                        <div
                            className={classNames(
                                styles['disclaimer-container'],
                                !secondaryInView &&
                                    styles['disclaimer-container--visible']
                            )}
                        >
                            <Markdown
                                markdown={disclaimer}
                                color='dim'
                                typeStyle={{
                                    _: 'body-2xs',
                                }}
                            />
                        </div>
                    </div>
                </div>
            )
        },
        {
            getHeaderHeight,
            includeScrollIn: true,
            includeScrollOut: true,
        }
    )
)

export default HomepageHero

export type HomepageHeroProps = Omit<HomepageHeroFragment, 'sys' | 'type'>
